import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  CloudArrowUp,
  ImageSquare,
  MagnifyingGlass,
  Play,
  Trash,
  VideoCamera,
  X,
} from '@phosphor-icons/react';
import { resolveAssetUrl } from '../../lib/utils';
import ContextHelpButton from '../ContextHelpButton';

const ACCEPTED_MEDIA = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime';

function isVideo(item) {
  return item?.type === 'video' || /\.(mp4|webm|mov)$/i.test(item?.filename || item?.url || '');
}

export default function MediaLibraryTab({ api, project }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadLibrary = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/media/library/${project.id}`);
      setItems(Array.isArray(data) ? data : []);
    } catch {
      toast.error(t('library.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(file => (
      file.type.startsWith('image/') || file.type.startsWith('video/')
    ));
    if (!files.length || uploading) return;
    const body = new FormData();
    files.forEach(file => body.append('files', file));
    setUploading(true);
    try {
      const { data } = await api.post(`/media/library/${project.id}`, body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const created = Array.isArray(data?.items) ? data.items : [];
      setItems(previous => [...created, ...previous]);
      toast.success(t('library.uploadSuccess', { count: created.length }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('library.uploadError'));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const deleteAsset = async (item) => {
    if (!item.library_asset || deletingId) return;
    if (!window.confirm(t('library.deleteConfirm'))) return;
    setDeletingId(item.id);
    try {
      await api.delete(`/media/library/${project.id}/${item.id}`);
      setItems(previous => previous.filter(asset => asset.id !== item.id));
      if (preview?.id === item.id) setPreview(null);
      toast.success(t('library.deleteSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('library.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return items.filter(item => {
      const video = isVideo(item);
      if (filter === 'image' && video) return false;
      if (filter === 'video' && !video) return false;
      if (!normalizedQuery) return true;
      return [item.original_name, item.filename, item.hook_text]
        .some(value => String(value || '').toLowerCase().includes(normalizedQuery));
    });
  }, [filter, items, query]);

  const counts = useMemo(() => ({
    all: items.length,
    image: items.filter(item => !isVideo(item)).length,
    video: items.filter(isVideo).length,
  }), [items]);

  return (
    <div className="pt-2 pb-10" data-testid="media-library-panel">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-5">
        <div className="flex items-start gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-purple)] font-semibold">
              {t('library.eyebrow')}
            </p>
            <h2 className="text-xl md:text-2xl font-bold mt-1">{t('library.title')}</h2>
            <p className="text-xs md:text-sm text-[var(--text-muted)] mt-1 max-w-2xl">{t('library.subtitle')}</p>
          </div>
          <ContextHelpButton articleId="media-library" label="Guida Media Library" />
        </div>
        <div className="text-xs text-[var(--text-muted)]">
          {t('library.items', { count: items.length })}
        </div>
      </div>

      <button
        type="button"
        className={`w-full rounded-2xl border border-dashed p-6 md:p-8 text-center transition-all ${dragging ? 'scale-[1.01]' : ''}`}
        style={{
          borderColor: dragging ? 'var(--accent-purple)' : 'rgba(124,92,255,0.34)',
          background: dragging
            ? 'linear-gradient(135deg, rgba(124,92,255,0.18), rgba(230,70,170,0.12))'
            : 'linear-gradient(135deg, rgba(124,92,255,0.08), rgba(255,255,255,0.02))',
        }}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          uploadFiles(event.dataTransfer.files);
        }}
        disabled={uploading}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_MEDIA}
          multiple
          className="hidden"
          onChange={event => uploadFiles(event.target.files)}
        />
        <span
          className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl"
          style={{ background: 'linear-gradient(135deg, rgba(124,92,255,0.28), rgba(230,70,170,0.2))' }}
        >
          <CloudArrowUp size={24} className={uploading ? 'animate-bounce' : ''} />
        </span>
        <p className="font-semibold">{uploading ? t('library.uploading') : t('library.dropTitle')}</p>
        <p className="text-xs text-[var(--text-muted)] mt-1">{t('library.dropHint')}</p>
      </button>

      <div className="flex flex-col md:flex-row gap-3 my-5">
        <div className="flex gap-2 overflow-x-auto">
          {[
            ['all', t('library.all'), counts.all],
            ['image', t('library.images'), counts.image],
            ['video', t('library.videos'), counts.video],
          ].map(([id, label, count]) => (
            <button
              key={id}
              className={`rounded-full px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors ${filter === id ? 'text-white' : 'text-[var(--text-muted)]'}`}
              style={{
                background: filter === id ? 'rgba(124,92,255,0.22)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${filter === id ? 'rgba(124,92,255,0.45)' : 'var(--border-color)'}`,
              }}
              onClick={() => setFilter(id)}
            >
              {label} <span className="opacity-65 ml-1">{count}</span>
            </button>
          ))}
        </div>
        <label className="relative flex-1 md:max-w-sm md:ml-auto">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            className="input-dark !pl-9 !py-2 text-xs"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={t('library.search')}
          />
        </label>
      </div>

      {loading ? (
        <div className="py-14 text-center text-sm text-[var(--text-muted)]">{t('library.loading')}</div>
      ) : filteredItems.length === 0 ? (
        <div className="rounded-2xl border border-[var(--border-color)] py-14 text-center">
          <ImageSquare size={34} className="mx-auto mb-3 text-[var(--text-muted)]" />
          <p className="font-semibold">{query || filter !== 'all' ? t('library.noResults') : t('library.empty')}</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">{t('library.emptyHint')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
          {filteredItems.map(item => {
            const video = isVideo(item);
            const url = resolveAssetUrl(item.url);
            return (
              <article
                key={`${item.source_kind}-${item.id}`}
                className="group overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-card)]"
              >
                <button
                  className="relative block w-full aspect-[4/5] overflow-hidden bg-black/35"
                  onClick={() => setPreview(item)}
                  title={t('library.preview')}
                >
                  {video ? (
                    <>
                      <video src={url} muted preload="metadata" className="h-full w-full object-cover" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/15">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black/60">
                          <Play size={18} weight="fill" />
                        </span>
                      </span>
                    </>
                  ) : (
                    <img src={url} alt={item.original_name || item.filename || ''} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  )}
                </button>
                <div className="p-2.5">
                  <p className="text-xs font-medium truncate" title={item.original_name || item.filename}>
                    {item.original_name || item.filename}
                  </p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="flex items-center gap-1 text-[9px] text-[var(--text-muted)]">
                      {video ? <VideoCamera size={11} /> : <ImageSquare size={11} />}
                      {item.library_asset ? t('library.sourceLibrary') : t('library.sourceContent')}
                    </span>
                    {item.library_asset && (
                      <button
                        className="ml-auto p-1 rounded text-[var(--accent-pink)] hover:bg-white/5"
                        onClick={() => deleteAsset(item)}
                        disabled={deletingId === item.id}
                        title={t('library.delete')}
                      >
                        <Trash size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {items.some(item => !item.library_asset) && (
        <p className="text-[10px] text-[var(--text-muted)] mt-4">{t('library.historicalHint')}</p>
      )}

      {preview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div className="relative max-h-[92vh] max-w-5xl" onClick={event => event.stopPropagation()}>
            <button
              className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--bg-card)] border border-[var(--border-color)]"
              onClick={() => setPreview(null)}
              title={t('library.closePreview')}
            >
              <X size={17} />
            </button>
            {isVideo(preview) ? (
              <video src={resolveAssetUrl(preview.url)} controls autoPlay className="max-h-[88vh] max-w-full rounded-2xl bg-black" />
            ) : (
              <img src={resolveAssetUrl(preview.url)} alt="" className="max-h-[88vh] max-w-full rounded-2xl object-contain" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
